// My Commissions widget — small block for advisor dashboards
// (Grant, Brandon, future Jake, Nate, Mark, etc.). Pulls
// /api/dashboard/my-commissions and renders YTD + pending + last
// paycheck for the current user.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  apiV11,
  type MyCommissionsResponse,
} from "../api";
import { formatDollars } from "../utils/formatting";

type LoadState = "loading" | "error" | "ready" | "empty";

export function MyCommissionsWidget() {
  const [state, setState] = useState<LoadState>("loading");
  const [data, setData] = useState<MyCommissionsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiV11
      .getMyCommissions()
      .then((res) => {
        if (cancelled) return;
        if (
          res.ytd_paid_cents === 0 &&
          res.pending_count === 0 &&
          res.latest_paid === null
        ) {
          setState("empty");
        } else {
          setData(res);
          setState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") {
    return (
      <WidgetShell title="My Commissions">
        <div style={{ color: "var(--dark-gray)", fontSize: 12 }}>Loading…</div>
      </WidgetShell>
    );
  }
  if (state === "error") {
    return (
      <WidgetShell title="My Commissions">
        <div style={{ color: "var(--danger)", fontSize: 12 }}>
          Couldn't load commissions.
        </div>
      </WidgetShell>
    );
  }
  if (state === "empty" || !data) {
    return (
      <WidgetShell title="My Commissions">
        <div style={{ color: "var(--dark-gray)", fontSize: 12 }}>
          No commission disbursements yet.
        </div>
      </WidgetShell>
    );
  }

  return (
    <WidgetShell title="My Commissions">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: "var(--dark-gray)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            YTD paid
          </div>
          <div
            style={{
              fontFamily: "Gotham, Montserrat, sans-serif",
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: "0.02em",
              color: "var(--navy)",
              marginTop: 2,
            }}
          >
            {formatDollars(data.ytd_paid_cents / 100)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--dark-gray)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Pending
          </div>
          <div
            style={{
              fontFamily: "Gotham, Montserrat, sans-serif",
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: "0.02em",
              color: "var(--navy)",
              marginTop: 2,
            }}
          >
            {formatDollars(data.pending_total_cents / 100)}
          </div>
          <div style={{ fontSize: 11, color: "var(--dark-gray)" }}>
            {data.pending_count} open
          </div>
        </div>
      </div>

      {data.latest_paid && (
        <div
          style={{
            borderTop: "1px solid var(--mid-gray)",
            paddingTop: 8,
            fontSize: 12,
            color: "var(--dark-gray)",
          }}
        >
          Last paycheck{" "}
          <strong style={{ color: "var(--navy)" }}>
            {formatDollars(data.latest_paid.amount_cents / 100)}
          </strong>{" "}
          · {new Date(data.latest_paid.paid_at).toLocaleDateString()}
          {data.latest_paid.reference && ` · #${data.latest_paid.reference}`}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <Link to="/my-commissions" style={{ fontSize: 11, color: "var(--blue-dark)" }}>
          View all →
        </Link>
      </div>
    </WidgetShell>
  );
}

function WidgetShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 16,
        background: "var(--bg-0)",
        border: "1px solid var(--mid-gray)",
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontSize: 12,
          color: "var(--navy)",
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// Standalone page for /my-commissions — simple history table.
export function MyCommissionsPage() {
  const [state, setState] = useState<LoadState>("loading");
  const [data, setData] = useState<MyCommissionsResponse | null>(null);

  useEffect(() => {
    apiV11
      .getMyCommissions()
      .then((res) => {
        setData(res);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, []);

  if (state === "loading" || !data) return <div style={{ padding: 32 }}>Loading…</div>;
  if (state === "error")
    return (
      <div style={{ padding: 32, color: "var(--danger)" }}>
        Couldn't load commissions.
      </div>
    );

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1000, margin: "0 auto" }}>
      <h1
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontSize: 22,
          color: "var(--navy)",
          margin: 0,
          marginBottom: 12,
        }}
      >
        My Commissions
      </h1>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <StatTile label="YTD paid" value={formatDollars(data.ytd_paid_cents / 100)} />
        <StatTile label="Pending" value={formatDollars(data.pending_total_cents / 100)} sub={`${data.pending_count} open`} />
        <StatTile
          label="Last paycheck"
          value={
            data.latest_paid
              ? formatDollars(data.latest_paid.amount_cents / 100)
              : "—"
          }
          sub={
            data.latest_paid
              ? new Date(data.latest_paid.paid_at).toLocaleDateString()
              : "none yet"
          }
        />
      </div>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "var(--bg-0)",
          border: "1px solid var(--mid-gray)",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <thead>
          <tr style={{ textAlign: "left", background: "var(--bg-1)", fontSize: 11, color: "var(--dark-gray)" }}>
            <th style={{ padding: "10px 12px" }}>Entity</th>
            <th style={{ padding: "10px 12px" }}>Company</th>
            <th style={{ padding: "10px 12px" }}>Period</th>
            <th style={{ padding: "10px 12px", textAlign: "right" }}>Net</th>
            <th style={{ padding: "10px 12px", textAlign: "right" }}>My share</th>
            <th style={{ padding: "10px 12px" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.recent.map((d) => (
            <tr key={d.id} style={{ borderTop: "1px solid var(--mid-gray)" }}>
              <td style={{ padding: "10px 12px" }}>
                {d.entity_type} · {d.entity_id.slice(0, 10)}…
              </td>
              <td style={{ padding: "10px 12px" }}>{d.company}</td>
              <td style={{ padding: "10px 12px", fontSize: 12 }}>
                {d.period_end ?? "—"}
              </td>
              <td style={{ padding: "10px 12px", textAlign: "right" }}>
                {formatDollars(d.net_income_cents / 100)}
              </td>
              <td
                style={{
                  padding: "10px 12px",
                  textAlign: "right",
                  color: "var(--navy)",
                  fontWeight: 600,
                }}
              >
                {formatDollars(d.advisor_disbursement_cents / 100)}
              </td>
              <td style={{ padding: "10px 12px", fontSize: 11 }}>
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    background: d.status === "paid" ? "var(--success-bg)" : "var(--bg-1)",
                    color: d.status === "paid" ? "var(--success)" : "var(--dark-gray)",
                  }}
                >
                  {d.status}
                </span>
              </td>
            </tr>
          ))}
          {data.recent.length === 0 && (
            <tr>
              <td
                colSpan={6}
                style={{ padding: 20, textAlign: "center", color: "var(--dark-gray)" }}
              >
                No disbursements yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      style={{
        padding: 16,
        background: "var(--bg-0)",
        border: "1px solid var(--mid-gray)",
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--dark-gray)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          fontSize: 24,
          letterSpacing: "0.02em",
          color: "var(--navy)",
          marginTop: 4,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "var(--dark-gray)", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
